// ExistingWorkspacePage.js — Clean redesign of /workspace/:content_id

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch, faTriangleExclamation, faCheck, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import AppTransition from '../../components/appComponents/AppTransition';
import UploadWorkspace from './workspaceUploads/uploadWorkspace';
import WorkspaceShortcutsPanel from './WorkspaceShortcutsPanel';
import WorkspaceChat from './chat/WorkspaceChat';
import AppLoader from '../../components/appComponents/AppLoader';
import InsufficientTimePopUp from '../../components/miscComponents/InsufficientTimePopUp';
import { registerListener } from '../../components/appComponents/ListenerManager';
import { generateShortcuts } from './generateShortcuts';
import usePlan, { isProPlan } from '../../myPlans/usePlan';
import PlanPurchasePage from '../../myPlans/PlanPurchasePage';
import { messageLimitFor } from './chat/chatModel';
import useOnboarding from '../onboarding/useOnboarding';
import { suggestionsFor } from '../onboarding/useCases';
import { EXAMPLES } from '../onboarding/examples';
import { track } from '../../analytics/track';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ExportMenu from './exports/ExportMenu';
import { trimmedPreviewNote } from './previewTrim';

import '../workspaceLayout.css';
import './existingWorkspacePage.css';

// ─── Status Badge ──────────────────────────────────────────────────────────────

const StatusBadge = ({
  isTranscriptAvailable, isLocalTranscriptAvailable,
  isYTProcessing, isLOCALProcessing,
  isTranscriptError,
}) => {
  if (isTranscriptError) return (
    <span className="ewp-badge ewp-badge--error">
      <FontAwesomeIcon icon={faTriangleExclamation} className="ewp-badge-icon" />
      Failed
    </span>
  );
  // A finished workspace is the normal state — only exceptions get a badge (same as the grid)
  if (isTranscriptAvailable || isLocalTranscriptAvailable) return null;
  if (isYTProcessing || isLOCALProcessing) return (
    <span className="ewp-badge ewp-badge--processing">
      <FontAwesomeIcon icon={faCircleNotch} spin className="ewp-badge-icon" />
      Processing
    </span>
  );
  return (
    <span className="ewp-badge ewp-badge--draft">Draft</span>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const ExistingWorkspacePage = () => {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const { content_id: rawContentId } = useParams();
  const content_id = rawContentId?.startsWith('workspace-')
    ? rawContentId.replace(/^workspace-/, '')
    : rawContentId;

  const [insufficientTime, setInsufficientTime] = useState(false);
  const plan = usePlan();
  const isPro = isProPlan(plan);
  const [onboarding] = useOnboarding();
  const [plansSource, setPlansSource] = useState(null);

  useEffect(() => {
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'subscriptionData', 'noTimeAvailable');
    const unsub = onSnapshot(ref, snap => {
      setInsufficientTime(snap.exists() && snap.data().insufficientTime);
    });
    registerListener(unsub);
    return () => unsub();
  }, [user]);

  // ── Metadata state ────────────────────────────────────────────────────────
  const [workspaceTitle,              setWorkspaceTitle]              = useState('');
  const [titleDraft,                  setTitleDraft]                  = useState(null); // non-null while renaming
  const [titleSaved,                  setTitleSaved]                  = useState(false);
  const titleCancelled = useRef(false); // Escape: the blur that follows must not save
  const [isTranscriptAvailable,       setIsTranscriptAvailable]       = useState(false);
  const [isLocalTranscriptAvailable,  setIsLocalTranscriptAvailable]  = useState(false);
  const [isYTProcessing,              setIsYTProcessing]              = useState(false);
  const [isLOCALProcessing,           setIsLOCALProcessing]           = useState(false);
  const [isTranscriptError,           setIsTranscriptError]           = useState(false);
  const [isTranscriptEmpty,           setIsTranscriptEmpty]           = useState(false);
  const [timestamps,                  setTimestamps]                  = useState('');
  const [isScRun,                     setisScRun]                     = useState(false);
  const [isScInProgress,              setIsScInProgress]              = useState(false);
  const [isScVideoPrivate,            setIsScVideoPrivate]            = useState(false);
  const [isApiVideo,                  setIsApiVideo]                  = useState(false);


  // Pre-chat answers and ratings on the workspace doc; null until the first snapshot.
  const [legacyChat,                  setLegacyChat]                  = useState(null);
  const [isYouTube,                   setIsYouTube]                   = useState(false);
  // A free upload preview (server-set): only its first chapters and a few messages until Pro
  const [isPreview,                   setIsPreview]                   = useState(false);
  const [previewLocked,               setPreviewLocked]               = useState(false);
  const [previewHidden,               setPreviewHidden]               = useState(0);
  const [previewError,                setPreviewError]                = useState(null);
  // A preview of a longer video covers its first 30 minutes; this is the video's full length
  const [previewFullSec,              setPreviewFullSec]              = useState(0);
  const [exampleId,                   setExampleId]                   = useState(null);
  const [durationSec,                 setDurationSec]                 = useState(0);
  const [ytVideoId,                   setYtVideoId]                   = useState(null);
  const [hasCaptionTranscript,        setHasCaptionTranscript]        = useState(false);

  const [loading,             setLoading]             = useState(true);

  // ── Hydrate metadata ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!content_id || !user) { setLoading(false); return; }

    const ref = doc(db, 'users', user.uid, 'workspaceContent', `workspace-${content_id}`);
    const localKey = `workspace-${content_id}-data`;
    const cached = localStorage.getItem(localKey);
    setLegacyChat(null);

    if (cached) {
      const d = JSON.parse(cached);
      setWorkspaceTitle(d.workspace_title || '');
      setIsTranscriptAvailable(d.is_transcript_available || false);
      setIsLocalTranscriptAvailable(d.is_local_transcript_available || false);
      setIsYTProcessing(d.is_caption_processing || false);
      setIsLOCALProcessing(d.is_local_caption_processing || false);
      setIsTranscriptError(d.is_transcript_error || false);
      setIsTranscriptEmpty(d.is_transcript_empty || false);
      setTimestamps(d.timestamps || '');
      setisScRun(d.is_sc_run || false);
      setIsScInProgress(d.sc_in_progress || false);
      setIsScVideoPrivate(d.sc_video_private || false);
      setIsApiVideo(d.origin === 'api');
      setLoading(false);
    }

    const unsub = onSnapshot(ref, snap => {
      if (snap.exists()) {
        const d = snap.data();
        setWorkspaceTitle(d.workspace_title || '');
        setIsTranscriptAvailable(d.is_transcript_available || false);
        setIsLocalTranscriptAvailable(d.is_local_transcript_available || false);
        setIsYTProcessing(d.is_caption_processing || false);
        setIsLOCALProcessing(d.is_local_caption_processing || false);
        setIsTranscriptError(d.is_transcript_error || false);
        setIsTranscriptEmpty(d.is_transcript_empty || false);
        setTimestamps(d.timestamps || '');
        setisScRun(d.is_sc_run || false);
        setIsScInProgress(d.sc_in_progress || false);
        setIsScVideoPrivate(d.sc_video_private || false);
        setIsApiVideo(d.origin === 'api');
        setIsYouTube(d.video_source === 'youtube');
        setIsPreview(!!d.is_preview);
        setPreviewLocked(!!d.preview_locked);
        setPreviewHidden(d.preview_hidden_chapters || 0);
        setPreviewError(d.preview_error || null);
        setPreviewFullSec(d.preview_full_seconds || 0);
        setExampleId(d.example_id || null);
        setDurationSec(d.video_duration_in_seconds || 0);
        setYtVideoId(d.yt_video_id || null);
        setHasCaptionTranscript(!!d.has_caption_transcript);
        setLegacyChat({
          prompt_map: d.prompt_map, output_map: d.output_map,
          chat_name_map: d.chat_name_map, feedback_map: d.feedback_map,
        });
      } else {
        localStorage.removeItem(localKey);
        navigate('/404-workspace');
      }
      setLoading(false);
    }, () => setLoading(false));

    return () => unsub();
  }, [content_id, user, navigate]);

  // ── A free upload preview unlocks itself once the account is Pro ────────────
  const unlockRequestedRef = useRef(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  useEffect(() => {
    if (!isPro || !previewLocked || unlockRequestedRef.current) return;
    unlockRequestedRef.current = true;
    setIsUnlocking(true);
    httpsCallable(getFunctions(), 'unlockWorkspacePreview')()
      .then(() => track('preview_unlocked'))
      .catch(() => { unlockRequestedRef.current = false; })
      .finally(() => setIsUnlocking(false));
  }, [isPro, previewLocked]);

  // ── Auto-trigger shortcuts ─────────────────────────────────────────────────
  const shortcutsTriggeredRef = useRef(false);
  useEffect(() => {
    if (
      !loading &&
      (isTranscriptAvailable || isLocalTranscriptAvailable) &&
      // API videos get their chapters started by the collector (get_captions/api_jobs.py), not by this page
      !isScRun && !isScInProgress && !isApiVideo &&
      !shortcutsTriggeredRef.current
    ) {
      shortcutsTriggeredRef.current = true;
      generateShortcuts(content_id, false).catch(() => {});
    }
  }, [isTranscriptAvailable, isLocalTranscriptAvailable, isScRun, isScInProgress, isApiVideo, loading, content_id]);

  // Cache what Firestore sent so the next visit paints immediately. Skipped while loading: writing
  // the initial defaults would make the next mount paint this workspace as an empty draft.
  useEffect(() => {
    if (loading) return;
    localStorage.setItem(`workspace-${content_id}-data`, JSON.stringify({
      workspace_title: workspaceTitle, is_transcript_available: isTranscriptAvailable,
      is_local_transcript_available: isLocalTranscriptAvailable,
      is_caption_processing: isYTProcessing, is_local_caption_processing: isLOCALProcessing,
      is_transcript_error: isTranscriptError, is_transcript_empty: isTranscriptEmpty,
      timestamps, is_sc_run: isScRun, sc_in_progress: isScInProgress, sc_video_private: isScVideoPrivate,
      origin: isApiVideo ? 'api' : null,
    }));
  }, [
    loading, content_id, workspaceTitle, isTranscriptAvailable, isLocalTranscriptAvailable,
    isYTProcessing, isLOCALProcessing, isTranscriptError, isTranscriptEmpty,
    timestamps, isScRun, isScInProgress, isScVideoPrivate, isApiVideo,
  ]);

  // ── Rename: saves on Enter / blur; Escape or an empty title reverts ─────────
  const commitTitle = async () => {
    const next = (titleDraft ?? '').trim();
    setTitleDraft(null);
    if (titleCancelled.current) { titleCancelled.current = false; return; }
    if (!next || next === workspaceTitle) return;
    setWorkspaceTitle(next);
    try {
      await updateDoc(doc(db, 'users', user.uid, 'workspaceContent', `workspace-${content_id}`), { workspace_title: next });
      localStorage.removeItem(`videoCache_${user.uid}_WorkspaceVideos`); // the /workspace grid caches titles
      setTitleSaved(true);
      setTimeout(() => setTitleSaved(false), 1500);
    } catch {
      setWorkspaceTitle(workspaceTitle);
    }
  };

  const shortcutProps = {
    isTranscriptAvailable: isTranscriptAvailable || isLocalTranscriptAvailable,
    isYTProcessing, isLOCALProcessing, isScRun, isScVideoPrivate,
    timestamps, content_id,
    previewLocked, previewHidden, isPro, isUnlocking,
    onUnlock: () => setPlansSource('preview_unlock'),
  };
  const trimNote = isPreview ? trimmedPreviewNote({ fullSeconds: previewFullSec, coveredSeconds: durationSec, isPro }) : null;
  const exampleUseCase = exampleId ? EXAMPLES[exampleId]?.useCase : null;
  const suggestions = suggestionsFor(onboarding?.useCase, exampleUseCase);
  const PREVIEW_ERRORS = {
    busy: 'Free previews are fully booked for today. Try again tomorrow, or go Pro to upload now.',
    used: 'You’ve already used your free preview. Go Pro to upload more of your own videos.',
    too_long: 'Videos can be up to 3 hours 30 minutes, on the free preview and on Pro.',
    error: 'Your free preview couldn’t start. Please try uploading again.',
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="ewp-page">
      {workspaceTitle && <Helmet><title>{`Bumpups | ${workspaceTitle}`}</title></Helmet>}
      {insufficientTime && <InsufficientTimePopUp />}

      <AppTransition>
        <div className="ewp-main">
          {loading ? <AppLoader inline /> : (
            <>
              {/* ── Header ── */}
              <div className="ewp-header">
                <div className="ewp-header-left">
                  <Link to="/workspace" className="ewp-back" aria-label="Back to Workspace" title="Back to Workspace">
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </Link>
                  <input
                    className="ewp-title-input"
                    value={titleDraft ?? workspaceTitle}
                    onFocus={() => setTitleDraft(workspaceTitle)}
                    onChange={e => setTitleDraft(e.target.value)}
                    onBlur={commitTitle}
                    onKeyDown={e => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                      if (e.key === 'Escape') { titleCancelled.current = true; e.currentTarget.blur(); }
                    }}
                    placeholder="Workspace title"
                    maxLength={100}
                    aria-label="Workspace title"
                  />
                  {titleSaved && <FontAwesomeIcon icon={faCheck} className="ewp-title-saved" aria-label="Saved" />}
                  <StatusBadge
                    isTranscriptAvailable={isTranscriptAvailable}
                    isLocalTranscriptAvailable={isLocalTranscriptAvailable}
                    isYTProcessing={isYTProcessing}
                    isLOCALProcessing={isLOCALProcessing}
                    isTranscriptError={isTranscriptError}
                  />
                </div>

                {(isTranscriptAvailable || isLocalTranscriptAvailable) && (
                  <ExportMenu
                    contentId={content_id}
                    title={workspaceTitle}
                    timestamps={timestamps}
                    videoUrl={isYouTube && ytVideoId ? `https://youtu.be/${ytVideoId}` : ''}
                    legacyChat={legacyChat}
                    // Uploads and long YouTube videos keep the pipeline's transcript; short ones (watched by Gemini
                    // directly) keep one from YouTube's captions when there are captions
                    transcriptAvailable={isLocalTranscriptAvailable || (isYouTube && (durationSec >= 1800 || hasCaptionTranscript))}
                    canFetchCaptions={isYouTube && durationSec < 1800 && !hasCaptionTranscript}
                    previewLocked={previewLocked && !isPro}
                    onUnlock={() => setPlansSource('export_unlock')}
                  />
                )}
              </div>

              {exampleId && (
                <div className="ewp-note">
                  <span><b>This is an example.</b> Ask it anything to see what Bump AI can do, then try your own video.</span>
                  <Link to="/workspace" className="ewp-note-btn">Add your own video</Link>
                </div>
              )}
              {previewError && PREVIEW_ERRORS[previewError] && (
                <div className="ewp-note ewp-note--warn" role="alert">
                  <span>{PREVIEW_ERRORS[previewError]}</span>
                  {previewError !== 'error' && previewError !== 'too_long' && (
                    <button type="button" className="ewp-note-btn" onClick={() => setPlansSource(`preview_${previewError}`)}>See Pro plans</button>
                  )}
                </div>
              )}
              {trimNote && (
                <div className="ewp-note">
                  <span><b>{trimNote.lead}</b>{trimNote.rest}</span>
                  {trimNote.action === 'plans'
                    ? <button type="button" className="ewp-note-btn" onClick={() => setPlansSource('preview_trimmed')}>See Pro plans</button>
                    : <Link to="/workspace" className="ewp-note-btn">Upload full video</Link>}
                </div>
              )}

              {/* ── Video + timestamps on the left, chat on the right ── */}
              <div className="ewp-layout">
                <aside className="ewp-side">

                {/* Video card */}
                <div className="ewp-card ewp-col-video">
                  <UploadWorkspace
                    isTranscriptAvailable={isTranscriptAvailable}
                    isLocalTranscriptAvailable={isLocalTranscriptAvailable}
                    isTranscriptEmpty={isTranscriptEmpty}
                    isYTProcessing={isYTProcessing}
                    isLOCALProcessing={isLOCALProcessing}
                    content_id={content_id}
                    isTranscriptError={isTranscriptError}
                  />
                </div>

                {/* Timestamps card */}
                <div className="ewp-card ewp-col-timestamps">
                  <WorkspaceShortcutsPanel section="timestamps" {...shortcutProps} />
                </div>

                </aside>

              {/* ── Chat ── */}
              <section className="ewp-chat">
                <WorkspaceChat
                  key={content_id}
                  content_id={content_id}
                  legacy={legacyChat}
                  isChatReady={isTranscriptAvailable || isLocalTranscriptAvailable}
                  isProcessing={isYTProcessing || isLOCALProcessing}
                  isScVideoPrivate={isScVideoPrivate}
                  isYouTube={isYouTube}
                  messageLimit={messageLimitFor(plan, { is_preview: isPreview })}
                  isPro={isPro}
                  isPreview={isPreview}
                  suggestions={suggestions}
                />
              </section>
              </div>
            </>
          )}
        </div>
      </AppTransition>
      {plansSource && <PlanPurchasePage source={plansSource} onClose={() => setPlansSource(null)} />}
    </div>
  );
};

export default ExistingWorkspacePage;
